import { type Notification, NOTIFICATION_TYPE_LABELS, type Page } from '@edms/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BellOff, CheckCheck } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/primitives';
import { Badge, EmptyState, Skeleton } from '@/components/ui/surfaces';
import { api } from '@/lib/api-client';
import { cn, formatRelative } from '@/lib/utils';

const SEVERITY_TONE = {
  INFO: 'info',
  WARNING: 'warning',
  CRITICAL: 'danger',
} as const;

/**
 * Centre de notifications (§14).
 *
 * Ouvrir le panneau marque implicitement comme lues les alertes affichées :
 * c'est le comportement attendu d'un centre de notifications, et cela évite un
 * geste supplémentaire dans une interface qui vise le minimum de clics (§19).
 */
export function NotificationsPanel({ onClose }: { onClose: () => void }): React.JSX.Element {
  const panelRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['notifications', 'list'],
    queryFn: () => api.get<Page<Notification>>('/notifications', { page: 1, pageSize: 15 }),
  });

  const markAllRead = useMutation({
    mutationFn: () => api.post('/notifications/mark-all-read'),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  useEffect(() => {
    const onPointerDown = (event: MouseEvent): void => {
      if (!panelRef.current?.contains(event.target as Node)) onClose();
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose]);

  const notifications = data?.items ?? [];
  const unreadCount = notifications.filter((notification) => !notification.readAt).length;

  return (
    <div
      ref={panelRef}
      className="anim-rise absolute right-0 z-30 mt-1.5 w-[22rem] overflow-hidden rounded-lg border border-line bg-raised shadow-[var(--shadow-overlay)]"
      role="dialog"
      aria-label="Notifications"
    >
      <header className="flex items-center justify-between border-b border-line-subtle px-4 py-3">
        <h2 className="text-sm font-semibold text-ink">Notifications</h2>
        {unreadCount > 0 && (
          <Button
            size="sm"
            variant="ghost"
            icon={<CheckCheck className="size-3.5" />}
            loading={markAllRead.isPending}
            onClick={() => markAllRead.mutate()}
          >
            Tout marquer lu
          </Button>
        )}
      </header>

      <div className="max-h-[26rem] overflow-y-auto">
        {isLoading ? (
          <div className="space-y-3 p-4">
            {[0, 1, 2].map((index) => (
              <div key={index} className="space-y-2">
                <Skeleton className="h-3 w-24" />
                <Skeleton className="h-3 w-full" />
              </div>
            ))}
          </div>
        ) : notifications.length === 0 ? (
          <EmptyState
            icon={BellOff}
            title="Aucune notification"
            description="Les incidents de numérisation, doublons et fins de lot apparaîtront ici."
          />
        ) : (
          <ul className="divide-y divide-line-subtle">
            {notifications.map((notification) => (
              <li key={notification.id}>
                <button
                  type="button"
                  disabled={!notification.actionPath}
                  onClick={() => {
                    if (notification.actionPath) {
                      navigate(notification.actionPath);
                      onClose();
                    }
                  }}
                  className={cn(
                    'w-full px-4 py-3 text-left transition-colors',
                    notification.actionPath ? 'hover:bg-hover' : 'cursor-default',
                    !notification.readAt && 'bg-accent-soft/40',
                  )}
                >
                  <div className="flex items-center justify-between gap-2">
                    <Badge tone={SEVERITY_TONE[notification.severity]} dot>
                      {NOTIFICATION_TYPE_LABELS[notification.type]}
                    </Badge>
                    <span className="shrink-0 text-2xs text-ink-faint">
                      {formatRelative(notification.createdAt)}
                    </span>
                  </div>
                  <p className="mt-1.5 text-xs leading-relaxed text-ink-muted">{notification.body}</p>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
